/**
 * Formatação das telas. Tudo que vem do backend é ISO-8601 com offset; a
 * exibição é sempre no fuso do navegador de quem está olhando.
 *
 * **O locale é o ativo, não `pt-BR` fixo** (Onda C · Lote 2a). Os três
 * formatadores eram constantes de módulo com `'pt-BR'` escrito dentro: além de
 * congelarem o idioma no import — como as tabelas de rótulo —, eles diriam
 * `26/08/2026` num console em inglês, onde `8/26/26` é o que se lê. O `Intl`
 * é caro de construir; o mapa abaixo guarda um formatador por idioma, sem
 * amarrar nenhum deles ao primeiro que aparecer.
 */

import { locale } from '@app/i18n';

const CACHE = new Map<string, Intl.DateTimeFormat>();

function formatador(nome: string, opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const atual = locale();
  const chave = atual + ':' + nome;
  let fmt = CACHE.get(chave);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(atual, opcoes);
    CACHE.set(chave, fmt);
  }
  return fmt;
}

const DATA_HOUR = () => formatador('dataHour', { dateStyle: 'short', timeStyle: 'short' });

/**
 * `2026-08-26T09:10:00-03:00` → `26/08/2026, 09:10`. Entrada inválida → `null`.
 *
 * A vírgula é do `Intl`, não nossa: `dateStyle` + `timeStyle` juntos separam
 * data e hora com ela em `pt-BR`. O comentário aqui dizia `26/08/2026 09:10` e
 * mentia sobre a própria saída — conferido no navegador contra o backend real.
 * Em `en` a mesma chamada devolve outra forma: quem decide é o locale.
 */
export function dataHour(iso?: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return DATA_HOUR().format(data);
}

const DATA = () => formatador('dataCurta', { dateStyle: 'short' });

/**
 * `2026-08-26T09:10:00-03:00` → `26/08/2026`. Entrada inválida → `null`.
 *
 * Só a data, sem hora: é o que a fila de T9 mostra ao lado da tag para separar
 * builds de dias diferentes (29/08). A hora do build raramente distingue o que a
 * data já não distinga, e ocupa a largura de que a tag precisa.
 */
export function dataCurta(iso?: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return DATA().format(data);
}

const DAY_MONTH = () => formatador('dayMonth', { day: '2-digit', month: '2-digit' });

/**
 * `2026-10-09T14:05:00-03:00` → `09/10`. Entrada inválida → `null`.
 *
 * Dia e mês, sem o ano: é o «lido em 09/10 14:05» da foto do runtime (aim-6ph.13,
 * decisão 7). A foto de outro dia é de ontem ou de poucos dias atrás, e o ano só
 * afastaria a hora, que é o que diz a idade dela.
 */
export function dayMonth(iso?: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return DAY_MONTH().format(data);
}

const HOUR = () => formatador('hour', { timeStyle: 'short' });

/**
 * `2026-08-29T14:32:10-03:00` → `14:32`. Entrada inválida → `null`.
 *
 * Só a hora: é o «Discovery já em andamento (desde 14:32)» de 29/08. A varredura
 * que está correndo começou há segundos ou minutos — repetir a data de hoje ali
 * só afastaria o número que interessa.
 */
export function hour(iso?: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return HOUR().format(data);
}

const HOUR_SECONDS = () => formatador('hourSeconds', { timeStyle: 'medium' });

/**
 * `2026-10-08T11:02:10-03:00` → `11:02:10`. Entrada inválida → `null`.
 *
 * A hora com os segundos: é a janela de uma conversa do Chat na faixa de T16 (aim-5wc, §3.5 do protótipo). A janela
 * tem um minuto e meio; sem os segundos, o início e o fim sairiam com a mesma hora.
 */
export function hourSeconds(iso?: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return HOUR_SECONDS().format(data);
}
